import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ImageWithFallback } from '../ImageWithFallback';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  branchsAPI,
  type CompanyBranch,
  type UpdateStockData,
} from '@/services/branchs';
import type { Product } from '@/types/products';
import { OverviewTab } from './tabs/OverviewTab';
import { UpdatePriceTab } from './tabs/UpdatePriceTab';
import { AdjustStockTab } from './tabs/AdjustStockTab';
import { Package, ChevronDown } from 'lucide-react';

interface StockManagementDialogProps {
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StockManagementDialog({
  product,
  open,
  onOpenChange,
}: StockManagementDialogProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [newPrice, setNewPrice] = useState(product?.price?.toString() || '');
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'remove'>('add');
  const [adjustmentQuantity, setAdjustmentQuantity] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // Load current user's branches
  const { data: myBranches = [] } = useQuery<CompanyBranch[]>({
    queryKey: ['myBranches'],
    queryFn: () => branchsAPI.getMyBranches(),
    enabled: open,
  });

  // Set default selected branch to first item
  useEffect(() => {
    if (myBranches.length > 0 && !selectedBranchId) {
      setSelectedBranchId(myBranches[0].id);
    }
  }, [myBranches, selectedBranchId]);

  const {
    data: stockData,
    isLoading,
    refetch: refetchStockData,
  } = useQuery({
    queryKey: ['productStock', selectedBranchId, product?.id],
    queryFn: () => {
      if (!product?.id || !selectedBranchId) return null;
      return branchsAPI.getProductStock(
        selectedBranchId,
        product.id.toString()
      );
    },
    enabled: open && !!product?.id && !!selectedBranchId,
  });

  const handleStockUpdate = async (payload: UpdateStockData) => {
    if (!selectedBranchId) {
      toast.error('Branch is not selected');
      return;
    }
    const operation = !stockData ? 'create' : 'update';

    console.log(operation);

    try {
      setIsSaving(true);

      let promise;
      if (operation === 'create') {
        promise = branchsAPI.createStock({
          ...payload,
          branchId: selectedBranchId,
          productId: product?.id?.toString() || '',
        });
      } else {
        const updatedPayload = {
          quantity: payload.quantity,
          price: payload.price,
          sellPrice: payload.sellPrice,
          isActive: payload.isActive,
        };
        promise = branchsAPI.updateStock(
          selectedBranchId,
          product?.id?.toString() || '',
          updatedPayload
        );
      }

      toast.promise(promise, {
        loading: 'Updating...',
        success: () => {
          refetchStockData();
          queryClient.invalidateQueries({
            queryKey: ['productStock', selectedBranchId, product?.id],
          });

          setNewPrice('');
          setAdjustmentQuantity('');
          setAdjustmentType('add');
          setActiveTab('overview');

          const action =
            activeTab === 'price' ? 'Price updated' : 'Stock updated';
          return `${action} successfully!`;
        },
        error: (error) => {
          console.error('Error updating stock:', error);
          return 'Failed to update. Please try again.';
        },
      });
    } catch (error) {
      console.error('Error in handleStockUpdate:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'add':
        return <span className="text-green-600">+</span>;
      case 'remove':
        return <span className="text-red-600">-</span>;
      case 'update':
        return <span className="text-blue-600">$</span>;
      default:
        return <span>•</span>;
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!product) return null;

  // Mock stock history data

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-[95vw] sm:max-w-3xl lg:!max-w-6xl h-[90vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6 pe-2 sm:pe-4">
            <div className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2 sm:gap-3 text-lg sm:text-xl">
                <Package className="h-5 w-5" />
                <span className="truncate">Stock & Pricing Management</span>
              </DialogTitle>
              <div className="flex items-center gap-1.5 sm:gap-2 mt-2 flex-wrap text-xs sm:text-sm">
                <span className="font-semibold truncate max-w-full sm:max-w-xs">
                  {product.name}
                </span>
                <span className="text-muted-foreground">•</span>
                <span className="text-muted-foreground truncate">
                  SKU: {product.sku}
                </span>
                {myBranches.length > 0 && (
                  <>
                    <span className="text-muted-foreground">•</span>
                    {myBranches.length === 1 ? (
                      <span className="text-muted-foreground truncate max-w-full sm:max-w-xs">
                        Branch: {myBranches[0].name}
                      </span>
                    ) : (
                      <div className="flex items-center gap-1 text-xs sm:text-[0.8rem]">
                        <span className="text-muted-foreground">Branch:</span>
                        <div className="relative inline-flex items-center">
                          <select
                            className="h-7 appearance-none rounded-md border border-input bg-muted px-2 pr-6 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring max-w-[9rem] sm:max-w-none"
                            value={selectedBranchId ?? ''}
                            onChange={(e) =>
                              setSelectedBranchId(e.target.value)
                            }
                          >
                            {myBranches.map((branch) => (
                              <option key={branch.id} value={branch.id}>
                                {branch.name}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-1 h-3 w-3 text-muted-foreground" />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="flex-shrink-0 w-14 h-14 sm:!w-20 sm:!h-20 p-1 rounded-lg overflow-hidden bg-muted border self-start sm:self-center">
              <ImageWithFallback
                isStorageUrl={
                  !product.images?.[0]?.originalUrl?.startsWith('http')
                }
                src={
                  product.images?.[0]?.originalUrl ||
                  product.image_urls?.[0] ||
                  ''
                }
                alt={product.name}
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex-1 flex flex-col px-4 sm:px-6 pb-4 sm:pb-6 overflow-hidden"
        >
          <TabsList className="mt-4 flex sm:grid sm:grid-cols-3 w-full overflow-x-auto sm:overflow-visible gap-2 sm:gap-0 scrollbar-thin">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="price">Update Price</TabsTrigger>
            <TabsTrigger value="stock">Adjust Stock</TabsTrigger>
          </TabsList>
          <TabsContent
            value="overview"
            className="space-y-4 mt-4 overflow-y-auto"
          >
            <OverviewTab
              stockData={stockData}
              formatDate={formatDate}
              getActionIcon={getActionIcon}
              isLoading={isLoading}
            />
          </TabsContent>

          {/* Update Price Tab */}
          <TabsContent value="price" className="space-y-4 mt-4 overflow-y-auto">
            <UpdatePriceTab
              stockData={stockData}
              newPrice={newPrice}
              onPriceChange={setNewPrice}
              onSave={handleStockUpdate}
              isSaving={isSaving}
            />
          </TabsContent>

          {/* Adjust Stock Tab */}
          <TabsContent value="stock" className="space-y-4 mt-4 overflow-y-auto">
            <AdjustStockTab
              stockData={stockData}
              adjustmentType={adjustmentType}
              adjustmentQuantity={adjustmentQuantity}
              onTypeChange={setAdjustmentType}
              onQuantityChange={setAdjustmentQuantity}
              onSave={handleStockUpdate}
              isSaving={isSaving}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
